import { pbQuery } from "@sergio9929/pb-query";
import { keepPreviousData, queryOptions } from "@tanstack/react-query";
import { pb } from "@/lib/pocketbase";
import type { SafeOmit } from "@/lib/utils";
import {
	type BranchesResponse,
	Collections,
	type DoctorBranchRolesResponse,
	DoctorBranchRolesRoleOptions,
	type DoctorsResponse,
} from "@/types/pocketbase.ts";
import type { ListOptions, OneOptions } from "@/types/query-options";

const COLLECTION = Collections.Branches;

export const branchesQueries = {
	all: () => [COLLECTION],
	lists: () => [...branchesQueries.all(), "list"],
	list: <Texpand = unknown>(params: ListOptions = { page: 1, perPage: 10, options: {} }) =>
		queryOptions({
			queryKey: [...branchesQueries.lists(), { page: params.page, perPage: params.perPage, options: params.options }],
			queryFn: () =>
				pb.collection(COLLECTION).getList<BranchesResponse<Texpand>>(params.page, params.perPage, params.options),
			placeholderData: keepPreviousData,
		}),
	fullLists: () => [...branchesQueries.lists(), "fullList"],
	fullList: () => {
		type ExpandBranchAndDoctor = {
			branch: BranchesResponse;
			doctor: DoctorsResponse;
		};

		type Search = SafeOmit<DoctorBranchRolesResponse, "branch" | "doctor"> & ExpandBranchAndDoctor;

		const query = pbQuery<Search>()
			.expand(["branch", "doctor"])
			.equal("role", DoctorBranchRolesRoleOptions.owner)
			.build((raw, params) => pb.filter(raw, params));

		return queryOptions({
			queryKey: [...branchesQueries.fullLists(), query],
			queryFn: async () => {
				const data = await pb
					.collection(Collections.DoctorBranchRoles)
					.getFullList<DoctorBranchRolesResponse<ExpandBranchAndDoctor>>({
						...query,
						sort: "-created",
					});

				return data.map((item) => ({
					...item.expand.branch,
					doctor: item.expand.doctor,
				}));
			},
			placeholderData: keepPreviousData,
		});
	},
	ones: () => [...branchesQueries.all(), "one"],
	one: <Texpand = unknown>(params?: OneOptions) =>
		queryOptions({
			queryKey: [...branchesQueries.ones(), params?.id, params?.options ?? {}],
			queryFn: () => pb.collection(COLLECTION).getOne<BranchesResponse<Texpand>>(params?.id ?? "", params?.options),
			enabled: Boolean(params?.id),
		}),
};
